import { Decorator } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { consentService, ConsentStatusEnum, EMAIL_CAMPAIGN_CONSENT_ID, PatientConsentExtendedModel, SMS_CAMPAIGN_CONSENT_ID } from 'src/services/consent';

// returns true if both campaigns have consent responses, otherwise false
function hasConsentsResponse(consents: PatientConsentExtendedModel[]): boolean {
    return consents.filter(c => [EMAIL_CAMPAIGN_CONSENT_ID, SMS_CAMPAIGN_CONSENT_ID].includes(c.consentId) && c.consentStatus !== ConsentStatusEnum.Unspecified).length === 2;
}

type Props = {
    prepend?: React.ReactNode;
    append?: React.ReactNode;
    className?: string;
    setIsDirty?(isDirty: boolean): void;
    shouldShowValidation?: boolean;
}

export interface ConsentModuleRef {
    submit(): Promise<ConsentStatusEnum | undefined>;
}

/** If consents have not previously been responded to, will update consents when module un-mounts */
export const ConsentModule = forwardRef<ConsentModuleRef, Props>(({ prepend, append, setIsDirty, shouldShowValidation }, ref) => {
    const [shouldShowConsent, setShouldShowConsent] = useState<boolean>();
    const [checkboxValue, setCheckboxValue] = useState<ConsentStatusEnum>();

    const { data: consents } = useAsyncData({
        service: consentService,
        method: consentService.GetPatientConsents,
        suppressToast: true,
    });

    const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
        setCheckboxValue(parseInt(e.target.value) as ConsentStatusEnum);
    }, []);

    const checkboxValueRef = useRef(checkboxValue);
    useEffect(() => {
        checkboxValueRef.current = checkboxValue;
    }, [checkboxValue])

    useEffect(() => {
        // if consents response changes, and we haven't already decided to show the module
        if (shouldShowConsent === undefined && consents) {
            setShouldShowConsent(!hasConsentsResponse(consents));
        }
    }, [consents, shouldShowConsent]);

    useEffect(() => {
        if (shouldShowConsent) {
            setIsDirty?.(checkboxValue === undefined);
        }
    }, [setIsDirty, shouldShowConsent, checkboxValue])

    useImperativeHandle(ref, () => ({
        submit: async () => {
            if (checkboxValueRef.current !== undefined) {
                return checkboxValueRef.current
            }
        }
    }));

    return <>
        {shouldShowConsent &&
            <>
                {prepend}
                <Decorator fieldset
                    label={<>
                        Do you agree to receive communications by email and SMS?
                        <br />
                        <small className='text-sm font-normal'>Consent is optional.</small>
                    </>} className=" mb-8"
                    fieldError={shouldShowValidation && checkboxValue === undefined ? {
                        message: "Please supply a response."
                    } : undefined}
                >
                    <div className="flex flex-row gap-5 text-base py-1.5">
                        <Decorator
                            className="flex flex-row gap-3">
                            <input
                                onChange={onChange}
                                checked={checkboxValue === ConsentStatusEnum.Granted}
                                value={ConsentStatusEnum.Granted}
                                type="radio"
                                className="radio align-bottom text-xl mt-0.5"
                                id="consentYes"
                            />
                            <label htmlFor="consentYes" className="text-gray-700 cursor-pointer">Yes</label>
                        </Decorator>
                        <Decorator
                            className="flex flex-row gap-3">
                            <input
                                onChange={onChange}
                                checked={checkboxValue === ConsentStatusEnum.Denied}
                                value={ConsentStatusEnum.Denied}
                                type="radio"
                                className="radio text-xl mt-0.5"
                                id="consentNo"
                            />
                            <label htmlFor="consentNo" className="text-gray-700 cursor-pointer">No</label>
                        </Decorator>
                    </div>
                </Decorator>
                {append}
            </>
        }
    </>
});